import moment from "moment";
import { LiabilityStatus } from "../../../types";
import {
  NextPaymentVariant,
  NextPaymentVariantParams,
  StatusVariant,
  TermInfo,
} from "./types";

export const getTermInfo = (
  startDate: string | undefined,
  endDate: string | undefined,
): TermInfo | null => {
  if (!startDate || !endDate) {
    return null;
  }

  const start = moment(startDate);
  const end = moment(endDate);
  if (!start.isValid() || !end.isValid()) {
    return null;
  }

  const totalYears = Math.round(end.diff(start, "years", true));
  const totalLabel = `${totalYears} ${totalYears === 1 ? "year" : "years"}`;

  const now = moment();
  if (end.isSameOrBefore(now)) {
    return { totalLabel, remainingLabel: "Completed" };
  }

  const totalMonths = end.diff(now, "months");
  const years = Math.floor(totalMonths / 12);
  const months = totalMonths % 12;
  const remainingLabel = `${years}y ${months}m left`;

  return { totalLabel, remainingLabel };
};

export const getStatusBadge = (
  status: LiabilityStatus,
  isActive: boolean,
): { variant: StatusVariant; label: string } => {
  if (status === LiabilityStatus.DEFAULTED) {
    return { variant: "defaulted", label: "Defaulted" };
  }
  if (status === LiabilityStatus.CLOSED) {
    return { variant: "completed", label: "Completed" };
  }
  if (!isActive) {
    return { variant: "inactive", label: "Inactive" };
  }
  return { variant: "active", label: "Active" };
};

// Priority: no-schedule > overdue > estimated > fixed — first matching condition wins.
export const getNextPaymentVariant = ({
  nextPaymentDate,
  isMissingPayment,
  isApproximatedAmount,
}: NextPaymentVariantParams): NextPaymentVariant => {
  if (!nextPaymentDate) {
    return "no-schedule";
  }
  if (isMissingPayment) {
    return "overdue";
  }
  if (isApproximatedAmount) {
    return "estimated";
  }
  return "fixed";
};

export const getOverdueDays = (dueDate: string): number => {
  const due = moment(dueDate).startOf("day");
  const today = moment().startOf("day");
  return Math.max(0, today.diff(due, "days"));
};

export const getOverdueLabel = (dueDate: string): string => {
  const days = getOverdueDays(dueDate);
  const unit = days === 1 ? "day" : "days";
  return `${days} ${unit} overdue`;
};
